import React, {createContext, useContext, useMemo} from "react";
import {getInMemoryBizFileUrlInfo} from "../../../../core/services/biz-file-urls/in-memory-biz-file-urls";
import {BizFileFileInfo, buildBizFilePreviewUrl, buildBizFilePublicPreviewUrl} from "../../../../core/services/biz-file-urls/biz-file-file-info";

export interface BizFormFileUrlsContextValue {
    /** When set (e.g. NCR subtree), preview URLs use this file info instead of the default operator BizForm memory channel. */
    fileInfoOverride: BizFileFileInfo | null;
}

const BizFormFileUrlsContext = createContext<BizFormFileUrlsContextValue | null>(null);

export interface BizFormFileUrlsProviderProps {
    /** Explicit BizFile credentials for this subtree (e.g. NCR BizForm deployment). */
    fileInfo?: BizFileFileInfo | null;
    children: React.ReactNode;
}

/**
 * Overrides BizForm file preview URLs for descendants (e.g. forms under NCR BizForm API).
 */
export const BizFormFileUrlsProvider: React.FC<BizFormFileUrlsProviderProps> = ({fileInfo, children}) => {
    const value = useMemo(() => ({fileInfoOverride: fileInfo || null}), [fileInfo]);
    return (
        <BizFormFileUrlsContext.Provider value={value}>
            {children}
        </BizFormFileUrlsContext.Provider>
    );
};

export interface UseBizFormFileUrlsResult {
    getFilePreviewUrl: (fileGuid: string, fileExtension?: string) => string | undefined;
    getPublicFilePreviewUrl: (fileGuid: string, fileExtension?: string) => string | undefined;
}

/**
 * Resolves BizForm-attached file preview URLs using {@link BizFormFileUrlsProvider} when present,
 * otherwise the in-memory operator BizForm channel (global bootstrap).
 */
export function useBizFormFileUrls(): UseBizFormFileUrlsResult {
    const ctx = useContext(BizFormFileUrlsContext);
    return useMemo(() => {
        const resolveInfo = (): BizFileFileInfo | null => {
            if (ctx?.fileInfoOverride != null) {
                return ctx.fileInfoOverride;
            }
            return getInMemoryBizFileUrlInfo('operatorBizForm');
        };
        return {
            getFilePreviewUrl: (fileGuid: string, fileExtension?: string) => buildBizFilePreviewUrl(resolveInfo(), fileGuid, fileExtension),
            getPublicFilePreviewUrl: (fileGuid: string, fileExtension?: string) => buildBizFilePublicPreviewUrl(resolveInfo(), fileGuid, fileExtension),
        };
    }, [ctx?.fileInfoOverride]);
}
